import { Button, Steps } from '@faceup/ui-base'
import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { FieldValues } from 'react-hook-form'
import { useFormLocalization } from '../FormLocalizationProvider'
import { Form, type FormProps } from './Form'
import { useFormSubmit } from './useFormSubmit'

// ==== StepForm Context API ====

type StepItem = { title: ReactNode; icon?: ReactNode; element?: ReactNode }

type ExternalSubmitFn = () => Promise<boolean>

type StepFormContextValue = {
  currentStep: number
  items: StepItem[]
  stepsCount: number
  setExternalSubmit: (fn: ExternalSubmitFn | null) => void
  requestStepChange: (targetStep: number) => Promise<void>
  onNext: () => Promise<void>
  onBack: () => Promise<void>
  onFinalSubmit: () => Promise<void>
  isFirstStep: boolean
  isLastStep: boolean
}

const StepFormContext = createContext<StepFormContextValue | null>(null)

const useStepForm = () => {
  const ctx = useContext(StepFormContext)
  if (!ctx) {
    throw new Error('useStepForm must be used within StepForm.Wrapper')
  }
  return ctx
}

type StepFormRootProps = {
  items: StepItem[]
  initialStep?: number
  /**
   * Called when all steps are completed and user submits the final step.
   * Use this to persist form data (e.g., to a mutation).
   * Data should be retrieved from your own provider/state management.
   */
  onSubmit: () => void | Promise<void>
}

const StepFormRoot = ({ items, initialStep = 0, onSubmit }: StepFormRootProps) => {
  const [currentStep, setCurrentStep] = useState(initialStep)
  const externalSubmitRef = useRef<ExternalSubmitFn | null>(null)
  const stepsCount = items.length
  const isFirstStep = currentStep === 0
  const isLastStep = currentStep === stepsCount - 1

  const setExternalSubmit = useCallback((fn: ExternalSubmitFn | null) => {
    externalSubmitRef.current = fn
  }, [])

  const runExternalSubmit = useCallback(async () => {
    if (!externalSubmitRef.current) {
      return true
    }
    try {
      const ok = await externalSubmitRef.current()
      return ok
    } catch {
      return false
    }
  }, [])

  const requestStepChange = useCallback(
    async (targetStep: number) => {
      const ok = await runExternalSubmit()
      if (!ok) {
        return
      }
      setCurrentStep(targetStep)
    },
    [runExternalSubmit]
  )

  const onFinalSubmit = useCallback(async () => {
    if (onSubmit) {
      await onSubmit()
    }
  }, [onSubmit])

  const onNext = useCallback(async () => {
    const ok = await runExternalSubmit()
    if (!ok) {
      return
    }
    const newStep = Math.min(currentStep + 1, stepsCount - 1)
    setCurrentStep(newStep)
  }, [runExternalSubmit, currentStep, stepsCount])

  const onBack = useCallback(async () => {
    const ok = await runExternalSubmit()
    if (!ok) {
      return
    }
    setCurrentStep(prev => Math.max(prev - 1, 0))
  }, [runExternalSubmit])

  const value = useMemo<StepFormContextValue>(
    () => ({
      currentStep,
      items,
      stepsCount,
      setExternalSubmit,
      requestStepChange,
      onNext,
      onBack,
      onFinalSubmit,
      isFirstStep,
      isLastStep,
    }),
    [
      currentStep,
      items,
      stepsCount,
      setExternalSubmit,
      requestStepChange,
      onNext,
      onBack,
      onFinalSubmit,
      isFirstStep,
      isLastStep,
    ]
  )

  const activeElement = items[currentStep]?.element

  return (
    <StepFormContext.Provider value={value}>
      <div className='flex flex-col gap-8'>
        <StepFormSteps />
        {activeElement ?? null}
      </div>
    </StepFormContext.Provider>
  )
}

const StepFormSteps = () => {
  const { currentStep, items, requestStepChange } = useStepForm()
  return (
    <Steps
      current={currentStep}
      onChange={step => void requestStepChange(step)}
      items={items.map(it => ({ icon: it.icon, title: it.title }))}
    />
  )
}

// ==== Per-step form component ====

type BaseFormStepProps<FV extends FieldValues = FieldValues> = Omit<
  FormProps<FV>,
  'submitButtonText' | 'customSubmitButtonText' | 'buttonsPosition' | 'additionalButtons'
>

type FormStepProps<FV extends FieldValues = FieldValues> = BaseFormStepProps<FV>

const FormStep = <FV extends FieldValues = FieldValues>({
  children,
  ...props
}: FormStepProps<FV>) => {
  const { submitButton } = useFormLocalization()
  const submitHook = useFormSubmit({
    form: props.form,
    onSubmit: props.onSubmit,
    isSuccessMessageDisplayed: false,
  })

  const stepForm = useStepForm()
  const isFirstStep = stepForm.isFirstStep
  const isLastStep = stepForm.isLastStep

  useEffect(() => {
    stepForm.setExternalSubmit(async () => {
      const result = await submitHook.submit()
      return result
    })
    return () => stepForm.setExternalSubmit(null)
  }, [stepForm, submitHook])

  // Navigation handlers from context if available
  const handleBack = async () => {
    await stepForm.onBack()
  }

  // Determine button texts based on step position
  const nextButtonText = isLastStep ? submitButton.save : 'Next'
  const backButtonText = 'Back'

  return (
    <Form
      {...props}
      isSubmitButtonDisabled={false}
      buttonsPosition='under'
      customSubmitButtonText={nextButtonText}
      isSuccessMessageDisplayed={false}
      additionalButtons={
        !isFirstStep ? (
          <Button onClick={() => void handleBack()}>{backButtonText}</Button>
        ) : undefined
      }
      onSubmit={async values => {
        const result = props.onSubmit(values)
        const isSuccess = !!(await Promise.resolve(result))
        if (isSuccess) {
          if (stepForm.isLastStep) {
            await stepForm.onFinalSubmit()
          } else {
            void stepForm.onNext()
          }
        }
        return isSuccess
      }}
    >
      {children}
    </Form>
  )
}

export const FormSteps = Object.assign((props: StepFormRootProps) => <StepFormRoot {...props} />, {
  Step: FormStep,
})
